import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, ShieldAlert, Activity } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function SalesContent() {
  return <div className="bg-cream-100 relative" data-id="element-50">
      {/* Divider */}
      <div className="h-px w-full bg-cream-300 absolute top-0" data-id="element-51" />

      {/* Opening Hook */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto text-left" data-id="element-52">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-53">
          <h2 className="text-2xl md:text-4xl font-bold text-ocean mb-4" data-id="element-54">
            Hay un momento que ningún padre olvida.
          </h2>

          <div className="space-y-4 text-sm md:text-lg text-ink-700 leading-relaxed text-left" data-id="element-55">
            <p data-id="element-56">
              Tu hijo llega a casa callado después de que las cosas no salieron
              bien. O explota por algo pequeño. O se rinde antes de intentarlo.
              O carga el peso de un error mucho después de que todo terminó.
            </p>
            <p className="text-sm md:text-base text-[#4A90C4] font-medium border-l-2 border-[#4A90C4] pl-4 py-1" data-id="element-57">
              Y tú quieres ayudarlo. Pero no sabes cómo.
            </p>
            <p data-id="element-58">
              No sabes qué decirle. No sabes si hablar o si darle espacio. No
              sabes si lo que haces está ayudando — o si sin querer, está
              empeorando las cosas.
            </p>
            <p data-id="element-59">
              Ese no saber — ese silencio entre los dos — es el partido
              invisible.
            </p>
            <p data-id="element-60">
              Y es el que realmente decide su trayectoria. No la técnica. No la
              velocidad.{' '}
              <span className="text-ink-900 font-semibold" data-id="element-61">
                Lo que lleva adentro — y lo que tú puedes hacer al respecto.
              </span>
            </p>
          </div>
        </motion.div>
      </section>

      {/* The Problem */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-cream-200/50 relative" data-id="element-62">
        <div className="max-w-4xl mx-auto" data-id="element-63">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeIn} className="text-left mb-8 md:mb-12" data-id="element-64">
            <ShieldAlert className="w-9 h-9 md:w-12 md:h-12 text-slate-teal mb-4" data-id="element-65" />
            <h2 className="text-2xl md:text-4xl font-bold text-ocean mb-4" data-id="element-66">
              El problema real del que nadie habla
            </h2>
            <p className="text-sm md:text-lg text-ink-700 max-w-2xl" data-id="element-67">
              La mayoría de los recursos sobre deporte juvenil hablan de
              táctica, físico, técnica. Muy pocos hablan de lo que realmente
              decide si un niño crece o se bloquea:{' '}
              <strong className="text-ink-900 font-medium" data-id="element-68">
                Lo que ocurre dentro de él.
              </strong>
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-69">
            <motion.div variants={fadeIn} className="bg-white p-4 md:p-8 rounded-xl border border-cream-300" data-id="element-70">
              <p className="text-xs md:text-base text-ink-700 leading-relaxed" data-id="element-71">
                Cuando un niño cree que su valor depende de hacerlo bien, el
                error se convierte en amenaza. Su cuerpo se tensa. Se encoge.{' '}
                <span className="text-ink-900 font-medium" data-id="element-72">
                  Juega para no perder en lugar de jugar para ganar.
                </span>
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="bg-white p-4 md:p-8 rounded-xl border border-cream-300" data-id="element-73">
              <p className="text-xs md:text-base text-ink-700 leading-relaxed" data-id="element-74">
                No porque sea débil. Porque su cerebro aprendió que equivocarse
                es peligroso. Y ese aprendizaje no ocurrió en el campo.{' '}
                <span className="text-ink-900 font-medium" data-id="element-75">
                  Ocurrió en casa.
                </span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quiénes somos */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-ocean" data-id="element-76">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-3xl mx-auto text-left" data-id="element-77">
          <h2 className="text-2xl md:text-4xl font-bold text-cream-100 mb-2" data-id="element-78">
            ¿Quiénes somos?
          </h2>
          <p className="text-xs md:text-sm text-cream-400 mb-6 italic" data-id="element-79">
            Por qué esto existe — y por qué es diferente
          </p>

          <div className="space-y-4 text-sm md:text-lg text-cream-200 leading-relaxed" data-id="element-80">
            <p data-id="element-81">
              Todos fuimos ese niño que no entendía lo que sentía. Y nuestros
              padres también — hicieron lo que pudieron con lo que tenían.
            </p>
            <p className="text-white font-medium" data-id="element-82">
              Nosotros necesitábamos esta guía.
            </p>
            <p data-id="element-83">
              Llevamos años estudiando neurociencia y el juego mental del
              rendimiento. No para acumular teoría — sino para entender qué le
              pasa a un niño por dentro cuando falla, se frustra o se bloquea.
            </p>
            <p data-id="element-84">
              Esto existe porque nadie lo estaba haciendo. Porque había un dolor
              real que no tenía respuesta — y decidimos ser los primeros en
              enfocarnos ahí.
            </p>
            <p data-id="element-85">
              Y lo más sorprendente: los padres que entran queriendo ayudar a su
              hijo — terminan siendo los primeros en transformarse.
            </p>
            <p className="text-white font-medium text-base md:text-xl mt-4" data-id="element-86">
              Nunca es tarde para abrir los ojos.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why what you tried didn't work */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto" data-id="element-87">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} data-id="element-88">
          <h2 className="text-2xl md:text-4xl font-bold text-ocean mb-6 text-center" data-id="element-89">
            ¿Por qué lo que has intentado no funciona?
          </h2>

          <ul className="space-y-4 md:space-y-6" data-id="element-90">
            <li className="flex items-start" data-id="element-91">
              <div className="flex-shrink-0 mt-1.5" data-id="element-92">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-slate-teal" data-id="element-93" />
              </div>
              <p className="ml-3 text-sm md:text-lg text-ink-700" data-id="element-94">
                <strong className="text-ink-900 block mb-0.5 font-medium text-sm md:text-base" data-id="element-95">
                  Más entrenamientos técnicos no eliminan el miedo al juicio.
                </strong>
                El techo real no está en sus piernas — está en lo que cree sobre
                sí mismo.
              </p>
            </li>
            <li className="flex items-start" data-id="element-96">
              <div className="flex-shrink-0 mt-1.5" data-id="element-97">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-slate-teal" data-id="element-98" />
              </div>
              <p className="ml-3 text-sm md:text-lg text-ink-700" data-id="element-99">
                <strong className="text-ink-900 block mb-0.5 font-medium text-sm md:text-base" data-id="element-100">
                  El análisis en el coche de vuelta
                </strong>
                Aunque sea bienintencionado, se recibe como juicio cuando el
                sistema nervioso todavía está dentro del partido.
              </p>
            </li>
            <li className="flex items-start" data-id="element-101">
              <div className="flex-shrink-0 mt-1.5" data-id="element-102">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-slate-teal" data-id="element-103" />
              </div>
              <p className="ml-3 text-sm md:text-lg text-ink-700" data-id="element-104">
                <strong className="text-ink-900 block mb-0.5 font-medium text-sm md:text-base" data-id="element-105">
                  Decirle "relájate" no funciona.
                </strong>
                Su cuerpo está en modo supervivencia. Necesita aprender a usar
                esa energía, no eliminarla.
              </p>
            </li>
          </ul>
        </motion.div>
      </section>

      {/* The Unique Mechanism */}
      <section className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-cream-200/30 relative overflow-hidden" data-id="element-106">
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-107">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-10" data-id="element-108">
            <h2 className="text-2xl md:text-4xl font-bold text-ocean mb-4" data-id="element-109">
              El Juego Invisible no es un libro sobre fútbol.
            </h2>
            <p className="text-base md:text-xl text-slate-teal font-medium" data-id="element-110">
              Es un protocolo de transformación mental basado en investigación
              real:
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-111">
            <motion.div variants={fadeIn} className="bg-white p-5 md:p-6 rounded-xl border border-cream-300" data-id="element-112">
              <Brain className="w-7 h-7 md:w-8 md:h-8 text-slate-teal mb-3" data-id="element-113" />
              <h3 className="text-ocean font-bold mb-1 text-sm md:text-base" data-id="element-114">
                Neuroplasticidad
              </h3>
              <p className="text-xs md:text-sm text-ink-500 leading-relaxed" data-id="element-115">
                El cerebro de tu hijo se moldea ahora más rápido que en
                cualquier otra etapa. Lo que repite hoy se instala mañana.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-5 md:p-6 rounded-xl border border-cream-300" data-id="element-116">
              <Activity className="w-7 h-7 md:w-8 md:h-8 text-slate-teal mb-3" data-id="element-117" />
              <h3 className="text-ocean font-bold mb-1 text-sm md:text-base" data-id="element-118">
                El principio de Hebb
              </h3>
              <p className="text-xs md:text-sm text-ink-500 leading-relaxed" data-id="element-119">
                Los patrones de miedo se formaron por repetición. Y por
                repetición consciente pueden transformarse.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-5 md:p-6 rounded-xl border border-cream-300" data-id="element-120">
              <Target className="w-7 h-7 md:w-8 md:h-8 text-slate-teal mb-3" data-id="element-121" />
              <h3 className="text-ocean font-bold mb-1 text-sm md:text-base" data-id="element-122">
                El experimento de Harvard
              </h3>
              <p className="text-xs md:text-sm text-ink-500 leading-relaxed" data-id="element-123">
                El cerebro no distingue entre práctica física y práctica mental
                vívida. Lo que tu hijo repite en su mente también entrena.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-5 md:p-6 rounded-xl border border-cream-300 lg:col-start-1 lg:col-end-3 lg:ml-auto lg:mr-3 lg:w-full max-w-md" data-id="element-124">
              <h3 className="text-ocean font-bold mb-1 text-sm md:text-base" data-id="element-125">
                El Efecto Pigmalión
              </h3>
              <p className="text-xs md:text-sm text-ink-500 leading-relaxed" data-id="element-126">
                Lo que tú crees sobre tu hijo determina, en parte, quién se
                convierte.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white p-5 md:p-6 rounded-xl border border-cream-300 lg:col-start-3 lg:col-end-4 lg:mr-auto lg:ml-3 lg:w-full max-w-md" data-id="element-127">
              <h3 className="text-ocean font-bold mb-1 text-sm md:text-base" data-id="element-128">
                La psicología del error
              </h3>
              <p className="text-xs md:text-sm text-ink-500 leading-relaxed" data-id="element-129">
                Cómo tu hijo interpreta el fallo decide si crece o se bloquea
                (Seligman).
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>;
}